import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Stack,
  Tab,
  TabList,
  Tabs,
  Table,
  Typography,
} from '@mui/joy';
import { Link, useNavigate } from 'react-router-dom';
import { documentDisplayRef } from '../../docs/utils/invoiceLedger';
import type { DirectoryAccount } from '../accounts.model';
import { accountsService } from '../accounts.service';
import {
  ARRANGEMENT_TYPE_LABELS,
  arrangementLedger,
  arrangementRecurringStatus,
  formatGbp,
  nextBillingPeriod,
  type ArrangementType,
  type BillingArrangement,
} from '../arrangements.model';
import { arrangementsService } from '../arrangements.service';
import { ArrangementFormModal, NewInvoiceModal, ledgerInvoices } from '../components/ArrangementForms';
import { ArrangementActionsMenu } from '../components/ArrangementActionsMenu';
import { BILLING_FREQUENCY_LABELS } from '../recurrence.model';

const ARRANGEMENT_TYPES = Object.keys(ARRANGEMENT_TYPE_LABELS) as ArrangementType[];
type StatusFilter = 'open' | 'due' | 'closed' | 'all';

/** Finance → Invoicing: every arrangement, each with its own KSU reference and invoices. */
const InvoicingPage: React.FC = () => {
  const navigate = useNavigate();
  const [arrangements, setArrangements] = useState<BillingArrangement[]>([]);
  const [accounts, setAccounts] = useState<DirectoryAccount[]>([]);
  const [invoicesById, setInvoicesById] = useState<Record<string, Array<Record<string, any>>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('open');
  const [typeFilter, setTypeFilter] = useState<ArrangementType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [creatingArrangement, setCreatingArrangement] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, accountRows] = await Promise.all([arrangementsService.list(), accountsService.list()]);
      setArrangements(rows);
      setAccounts(accountRows);
      setInvoicesById(await arrangementsService.invoicesByArrangement(rows));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoicing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const accountById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
  const counts = useMemo(() => ({
    open: arrangements.filter((row) => row.status === 'open').length,
    due: arrangements.filter((row) => arrangementRecurringStatus(row) === 'due').length,
    closed: arrangements.filter((row) => row.status === 'closed').length,
    all: arrangements.length,
  }), [arrangements]);

  const visible = arrangements.filter((arrangement) => {
    if (status === 'due' && arrangementRecurringStatus(arrangement) !== 'due') return false;
    if ((status === 'open' || status === 'closed') && arrangement.status !== status) return false;
    const account = accountById.get(arrangement.accountId);
    if (typeFilter !== 'all' && arrangement.arrangementType !== typeFilter) return false;
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return [arrangement.lifecycleRef, arrangement.title, account?.companyName]
      .some((value) => String(value || '').toLowerCase().includes(needle));
  });

  const addAccount = (account: DirectoryAccount) => {
    setAccounts((current) => [...current, account].sort((a, b) => a.companyName.localeCompare(b.companyName)));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography level="h3">Invoicing</Typography>
              <Chip size="sm" variant="soft">Keystone South UK · KSU</Chip>
            </Stack>
            <Typography level="body-sm" textColor="neutral.600" sx={{ maxWidth: 720 }}>
              Invoices to partner trades, tenants and suppliers. An arrangement is what you are billing someone for.
              It has its own KSU reference, and one or more invoices numbered from it.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Button onClick={() => setCreatingArrangement(true)}>New arrangement</Button>
            <Button variant="outlined" onClick={() => setCreatingInvoice(true)}>One-off invoice</Button>
          </Stack>
        </Stack>

        {error && <Alert color="danger">{error}</Alert>}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <Tabs value={status} onChange={(_, value) => setStatus((value as StatusFilter) || 'open')} sx={{ bgcolor: 'transparent' }}>
            <TabList>
              <Tab value="open">Open ({counts.open})</Tab>
              <Tab value="due" color={counts.due > 0 ? 'warning' : undefined}>Due ({counts.due})</Tab>
              <Tab value="closed">Closed ({counts.closed})</Tab>
              <Tab value="all">All ({counts.all})</Tab>
            </TabList>
          </Tabs>
          <Box sx={{ flex: 1 }} />
          <Select value={typeFilter} onChange={(_, value) => setTypeFilter((value as ArrangementType | 'all') || 'all')} sx={{ minWidth: 180 }}>
            <Option value="all">All types</Option>
            {ARRANGEMENT_TYPES.map((type) => <Option key={type} value={type}>{ARRANGEMENT_TYPE_LABELS[type]}</Option>)}
          </Select>
          <Input placeholder="Search ref, title or account" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: 260 }} />
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table hoverRow sx={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Ref</th>
                  <th>Arrangement</th>
                  <th style={{ width: 130 }}>Type</th>
                  <th>Account</th>
                  <th>Invoices</th>
                  <th style={{ width: 200 }}>Invoiced</th>
                  <th style={{ width: 150 }}>Next invoice</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 48 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map((arrangement) => {
                  const invoices = invoicesById[arrangement.id] || [];
                  const ledger = arrangementLedger(arrangement, ledgerInvoices(invoices));
                  const account = accountById.get(arrangement.accountId);
                  const progress = ledger.agreedGross ? Math.min(100, (ledger.invoicedGross / ledger.agreedGross) * 100) : null;
                  const live = invoices.filter((invoice) => invoice.status !== 'void');
                  return (
                    <tr
                      key={arrangement.id}
                      onClick={() => navigate(`/invoicing/${arrangement.lifecycleRef}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><Typography level="body-sm" fontWeight="lg">{arrangement.lifecycleRef}</Typography></td>
                      <td><Typography level="body-sm">{arrangement.title}</Typography></td>
                      <td><Chip size="sm" variant="soft">{ARRANGEMENT_TYPE_LABELS[arrangement.arrangementType]}</Chip></td>
                      <td><Typography level="body-sm">{account?.companyName || 'Unknown account'}</Typography></td>
                      <td>
                        <Typography level="body-sm">
                          {live.length === 0 ? '—' : live.map((invoice) => documentDisplayRef({
                            kind: 'Invoice',
                            invoiceRef: invoice.invoice_ref,
                            lifecycleRef: invoice.lifecycle_ref,
                          }).replace(`${arrangement.lifecycleRef}-`, '-')).join(', ')}
                        </Typography>
                      </td>
                      <td>
                        <Typography level="body-sm">
                          {formatGbp(ledger.invoicedGross)}
                          {ledger.agreedGross != null && <Typography textColor="neutral.500"> of {formatGbp(ledger.agreedGross)}</Typography>}
                        </Typography>
                        {progress != null && <LinearProgress determinate value={progress} size="sm" sx={{ mt: 0.5 }} />}
                      </td>
                      <td>
                        {(() => {
                          if (arrangement.frequency === 'none') return <Typography level="body-xs" textColor="neutral.500">As needed</Typography>;
                          const next = nextBillingPeriod(arrangement);
                          const recurring = arrangementRecurringStatus(arrangement);
                          return (
                            <Stack spacing={0.25}>
                              <Typography level="body-sm">{next ? next.start : 'Ended'}</Typography>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography level="body-xs" textColor="neutral.500">{BILLING_FREQUENCY_LABELS[arrangement.frequency]}</Typography>
                                {recurring === 'due' && <Chip size="sm" color="warning" variant="soft">Due</Chip>}
                              </Stack>
                            </Stack>
                          );
                        })()}
                      </td>
                      <td>
                        <Chip size="sm" variant="soft" color={arrangement.status === 'open' ? 'primary' : 'neutral'}>
                          {arrangement.status === 'open' ? 'Open' : 'Closed'}
                        </Chip>
                      </td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <ArrangementActionsMenu
                          arrangement={arrangement}
                          invoices={invoices}
                          onUpdated={(updated) => setArrangements((current) => current.map((row) => (row.id === updated.id ? updated : row)))}
                          onDeleted={(id) => setArrangements((current) => current.filter((row) => row.id !== id))}
                        />
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={9}>
                      <Typography level="body-sm" textColor="neutral.500" sx={{ py: 3, textAlign: 'center' }}>
                        {arrangements.length === 0
                          ? 'No arrangements yet. Start one with New arrangement, or raise a single One-off invoice.'
                          : `No ${status === 'all' ? '' : `${status} `}arrangements match.`}
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Sheet>
        )}
        <Typography level="body-xs" textColor="neutral.500">
          Accounts are managed in <Link to="/directory">Directory → Accounts</Link>.
        </Typography>
      </Stack>

      <ArrangementFormModal
        open={creatingArrangement}
        accounts={accounts}
        onAccountCreated={addAccount}
        onClose={() => setCreatingArrangement(false)}
        onSaved={(arrangement) => {
          setCreatingArrangement(false);
          navigate(`/invoicing/${arrangement.lifecycleRef}`);
        }}
      />
      <NewInvoiceModal
        open={creatingInvoice}
        accounts={accounts}
        onAccountCreated={addAccount}
        onClose={() => setCreatingInvoice(false)}
        onCreated={({ arrangement }) => {
          setCreatingInvoice(false);
          navigate(`/invoicing/${arrangement.lifecycleRef}`);
        }}
      />
    </Box>
  );
};

export default InvoicingPage;
